<template>
  <div class="rec-feeds">
    <div
      ref="contentRef"
      class="content"
      @scroll="throttleOnScroll"
    >
      <div class="btns">
        <div
          class="replay"
          @click="replay"
        >
          <i /><span>再看一次</span>
        </div>
        <div
          class="open-app"
          @click="openApp(false, true)"
        >
          <i />
          <span>
            {{ shouldShowDownloadText && !miniAppPathClickOpen ? '下载微视' : '打开微视' }}
          </span>
          <JumpMiniApp
            v-if="miniAppPathClickOpen"
            :path="miniAppPathClickOpen"
            @launch="openApp(false, true, true)"
          />
        </div>
      </div>
      <div class="title">
        {{ title }}
      </div>
      <ul class="list">
        <RecItem
          v-for="(feed, index) in recFeeds"
          :key="feed.id"
          :feed="feed"
          :recommendtags="recommendtags"
          :default-tag-img="configRecPage?.defaultLabelImg"
          @on-click-feed="(feed: typeof recFeeds[0], clickMiniApp: boolean) => onClickFeed(feed, index, clickMiniApp)"
        />
      </ul>
      <div class="swipper-for-bottom">
        <div
          v-if="loadingEnd"
          class="btn-jump-weishi"
          @click="openApp(true, false)"
        >
          去微视看更多精彩视频
          <JumpMiniApp
            v-if="miniAppPathClickMore"
            :path="miniAppPathClickMore"
            @launch="openApp(false, true, true)"
          />
        </div>
        <div
          v-if="!loadingEnd"
          class="loadingText"
        >
          正在努力加载
        </div>
      </div>
    </div>
    <Download
      v-if="downloadVisible"
      :should-show-download-text="shouldShowDownloadText"
      @on-download="onDownload"
      @on-close="onClose"
    />
  </div>
</template>

<script lang="ts" setup>
import { toRefs, ref, computed, inject, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from '@/store/index';
import throttle from 'lodash/throttle.js';
import fetchZzConfig from '@/services/fetch-zz-config';
import { ModelMetaFeed } from '@/types/model';
import { ZzConfig } from '@/types/services/fetch-zz-config';
import { IHashMap } from '@/types/data';
import { EMiniAppPosition } from '@/constants/mini-app';
import { PROVIDED_URL_INFO_NAME, TUrlInfo } from '@/composable/use-provide-url-info';
import { PROVIDED_BROWSER_INFO_NAME } from '@/composable/use-provide-browser-info';
import useGetCommonMethods from '@/composable/use-get-common-method';
import useRequestDataMethods from '@/composable/use-request-data-method';
import { userRecFeedsReport } from '@/composable/use-compoent-report-methods';
import { Browser } from '@/utils/no-import-meta/get-browser-obj';
import JumpMiniApp from '@/components/common/jump-mini-app.vue';
import Download from './download.vue';
import RecItem from './item.vue';

const emits = defineEmits(['on-replay']);

// props
interface Props {
  showRec: boolean,
  installed: boolean | null,
}
const props = withDefaults(defineProps<Props>(), {
  showRec: false,
  recFeeds: () => ([]),
  installed: false,
});
const {
  showRec,
  installed,
} = toRefs(props);

// data
const downloadTimer = ref(-999);   // 默认-999避免和已存在的timer冲突
const downloadVisible = ref(false);
const throttleOnScroll = ref(() => {});
const contentRef = ref<null | HTMLElement>(null);
const zzConfig = ref<null | ZzConfig>(null);
const loadingEnd = ref(false);
const attachInfo = ref<string>('{ "page": 1, "total_num": 12 }');
const recFeeds = ref<ModelMetaFeed[]>([]);
const currentRecFeedPageData = ref<ModelMetaFeed[]>([]);
const recommendtags = ref<IHashMap>();

// setup
const urlInfo = inject<TUrlInfo>(PROVIDED_URL_INFO_NAME);
const browserInfo = inject<Browser>(PROVIDED_BROWSER_INFO_NAME);
const {
  getMiniAppPath,
  openSchemaCheckInstallInQQAppendParams,
  openSchemaAppendParamsClipboard,
} = useGetCommonMethods();
const { fetchRecFeeds } = useRequestDataMethods();
const recFeedsReport = userRecFeedsReport({ zzConfig, recFeeds, showRec, currentRecFeedPageData });

// state
const { state } = useStore();
const growthTestData = computed(() => state.growthTestData);
const isCollection = computed(() => state.isCollection);

// computed
const shouldShowDownloadText = computed(() => installed.value && browserInfo?.client === 'qq');
const miniAppPathClickMore = computed(() => getMiniAppPath(EMiniAppPosition.jumpWeishi, { outFrom: 10001 }));
const miniAppPathClickOpen = computed(() => getMiniAppPath(EMiniAppPosition.recommendOpen, { outFrom: 10001 }));
const configRecPage = computed(() => zzConfig.value?.recPage || { defaultLabelImg: '', videoTagReportMap: [], recTitle: '', recMaxNum: 20 });
const friendPopUp = computed(() => zzConfig.value?.friendPopUps || { delayTime: 15, isShowPopUps: true });
const title = computed(() => configRecPage.value?.recTitle || '热门推荐');

// methods
const onClickFeed = (feed: ModelMetaFeed, index: number, clickMiniApp: boolean) => {
  if (!clickMiniApp) {
    openSchemaCheckInstallInQQAppendParams(true, isCollection.value, 'feed', {
      feed_id: feed.id,
    });
  }
  recFeedsReport.onClickFeed(feed, index);
};
const openApp = (jump: boolean, canDownload: boolean, clickMiniApp?: boolean) => {
  const { id: feedId } = urlInfo?.query || {};
  if (!clickMiniApp) {
    if (canDownload) {
      openSchemaAppendParamsClipboard(true, 'feed', {
        feed_id: feedId,
      });
    } else {
      openSchemaCheckInstallInQQAppendParams(true, isCollection.value, 'feed', {
        feed_id: feedId,
      });
    }
  }
  recFeedsReport.openApp(jump);
};
const replay = () => {
  const { id: feedId } = urlInfo?.query || {};
  emits('on-replay');
  openSchemaCheckInstallInQQAppendParams(true, isCollection.value, 'feed', {
    feed_id: feedId,
  });
  recFeedsReport.report('recommend.again.click');
};
const onDownload = () => {
  openSchemaAppendParamsClipboard(true, 'recommend');
  recFeedsReport.report('friendsfloat.open.click');
};
const onClose = () => {
  downloadVisible.value = false;
  recFeedsReport.report('friendsfloat.close.click');
};
const initDownloadUi = () => {
  if (!friendPopUp.value.isShowPopUps) {
    return;
  }
  clearTimeout(downloadTimer.value);
  downloadTimer.value = window.setTimeout(() => {
    downloadVisible.value = true;
    recFeedsReport.report('friendsfloat.open.exposure');
  }, friendPopUp.value.delayTime * 1000);
};
const onScroll = () => {
  const content = contentRef.value;
  if (!content) return;
  if (content.scrollHeight - content.offsetHeight - content.scrollTop < window.innerWidth) {
    if (!loadingEnd.value) {
      getRecFeeds();
    }
  }
};
const getRecFeeds = async () => {
  // 分页逻辑 和兼容 滚动效果 12条数据为一页
  const recMaxNum = 50 || configRecPage.value?.recMaxNum || 12;
  // 一次拉取的个数
  const fetchCount = 30;

  const res = await fetchRecFeeds({
    dataSource: growthTestData.value?.dataSource || 0,
    feedCount: fetchCount,
    attachInfo: attachInfo.value,
    playStatus: 1, // 0: 未播放， 1: 播放中
  });
  const loadedCount = res?.recommendFeeds.length || 0; // 加载的feed数量
  const feedCount = recFeeds.value.length; // 当前feed数量
  // 没有加载到数据
  if (!res || !loadedCount) {
    loadingEnd.value = true;
    return;
  }
  currentRecFeedPageData.value = res.recommendFeeds;
  // 是否还有剩下的feed需要加载
  const isRemain = recMaxNum - feedCount - loadedCount > 0;
  if (isRemain) {
    recFeeds.value = recFeeds.value.concat(res.recommendFeeds || []);
  } else {  // 如果数量足够了
    recFeeds.value = recFeeds.value.concat(res.recommendFeeds.slice(0, recMaxNum - feedCount) || []);
    loadingEnd.value = true;
  }
  attachInfo.value = res.attachInfo || '';
  return res;
};


onMounted(async () => {
  // 初始化zzConfig
  zzConfig.value = (await fetchZzConfig({
    key: 1754,  // 配置中心key
    flag: 1,    // 配置中心的标识
  }));
  // 初始化rec-feeds
  recommendtags.value = (await getRecFeeds())?.recommendTags; // 此组件不在首屏中展示，所以二个服务可串行执行
  // 初始化弹窗ui
  initDownloadUi();
  // 初始化灯塔上报
  recFeedsReport.init();
  // 设置滚动加载
  throttleOnScroll.value = throttle(onScroll, 200);
});
onBeforeUnmount(() => {
  clearTimeout(downloadTimer.value);
});
</script>

<style lang="scss" scoped>
.rec-feeds {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 20;
  background: rgba(0, 0, 0, 0.9);
  .content{
    height: 100%;
    overflow-x: hidden;
    overflow-y: auto;
  }
  .btns {
    display: flex;
    position: relative;
    align-items: center;
    justify-content: space-between;
    @include responsiveStyleSingle(width, 72, 'vw');
    @include responsiveStyleSingle(margin-top, 8,'vw');
    @include responsiveStyleSingle(margin-bottom, 8.4,'vw');
    margin-left: auto;
    margin-right: auto;
    & > div {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 1;
      @include responsiveStyleSingle(padding-left, 2.6667,'vw');
      @include responsiveStyleSingle(padding-right, 2.6667,'vw');
      @include responsiveStyleSingle(line-height, 10.6667, 'vw');
      @include responsiveStyleSingle(font-size, 3.7333, 'vw');
      @include responsiveStyleSingle(border-radius, 10.6667, 'vw');
      i {
        @include responsiveStyleSingle(margin-right, 2.6667, 'vw');
        background-image: url('http://qzonestyle.gtimg.cn/qz-proj/weishi-mobile/sprite/page-share-play-190514164234@2x.png');
      }
    }
    .replay {
      @include responsiveStyleSingle(margin-right, 3.2, 'vw');
      background: #2f2f2f;
      i {
        @include responsiveStyleSingle(width, 5.3333, 'vw');
        @include responsiveStyleSingle(height, 5.3333, 'vw');
        @include responsiveStyleDouble(background-position, -804, -364, 'px');
        @include responsiveStyleDouble(background-size, 850, 724, 'px');
      }
    }
    .open-app {
      position: relative;
      background-image: linear-gradient(39deg, #672bff 0, #fb4de6 200%);
      i {
        @include responsiveStyleSingle(width, 6.9333, 'vw');
        @include responsiveStyleSingle(height, 6.9333, 'vw');
        @include responsiveStyleDouble(background-position, -208, -618, 'px');
        @include responsiveStyleDouble(background-size, 850, 724, 'px');
      }
    }
  }
  .title {
    @include responsiveStyleSingle(margin-top, 8, 'vw');
    @include responsiveStyleSingle(margin-bottom, 4.2667, 'vw');
    @include responsiveStyleSingle(margin-left, 3.4667, 'vw');
    @include responsiveStyleSingle(font-size, 4.8, 'vw');
    font-weight: bold;
  }
  .list {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    @include responsiveStyleSingle(padding-left, 3.4667,'vw');
    @include responsiveStyleSingle(padding-right, 3.4667,'vw');
  }
  .btn-jump-weishi {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    @include responsiveStyleSingle(margin-top, 5.3333, 'vw');
    @include responsiveStyleSingle(margin-bottom, 16.8, 'vw');
    margin-left: auto;
    margin-right: auto;
    @include responsiveStyleSingle(width, 60, 'vw');
    @include responsiveStyleSingle(height, 12, 'vw');
    background: #7a46ff;
    @include responsiveStyleSingle(border-radius, 5.8667, 'vw');
    @include responsiveStyleSingle(font-size, 4.8, 'vw');
  }
  .loadingText {
    color: #6f6f6f;
    text-align: center;
  }
  .swipper-for-bottom {
    @include responsiveStyleSingle(width, 100, 'vw');
    @include responsiveStyleSingle(height, 28.8, 'vw');
  }
}
</style>
